import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { useAuthStore, useEmailConfirmationDialog } from 'services/hooks';
import { selectEmailConfirmed } from 'services/store/user/selectors';
import songList from 'services/locales/en/songList';
import AddToLibraryIcon from 'assets/icons/AddToLibrary/AddToLibraryIcon';
import BaseIconComponent from '../BaseIconComponent';

type PropsType = {
  isInLibrary: boolean;
  removeFromLibrary: () => void;
  addToLibrary: () => void;
};

export const DisplayAddToLibraryIcon = (
  props: PropsType
): React.ReactElement => {
  const { isInLibrary, removeFromLibrary, addToLibrary } = props;

  const { t } = useTranslation();

  const { authStore } = useAuthStore();
  const { isLoggedIn } = authStore;

  const isEmailConfirmed = useSelector(selectEmailConfirmed);
  const {
    onEmailConfirmationDialogShouldOpen,
    displayEmailConfirmationDialog,
  } = useEmailConfirmationDialog();

  const title = useMemo(() => {
    if (isInLibrary) {
      return t(songList.actions.deleteSong);
    }
    return t(songList.actions.addToLibrary);
  }, [t, isInLibrary]);

  const shouldDisplayEmailConfirmationDialog = useMemo(() => {
    if (!isLoggedIn) {
      return false;
    }
    return !isEmailConfirmed;
  }, [isLoggedIn, isEmailConfirmed]);

  const handleClick = useCallback(() => {
    if (shouldDisplayEmailConfirmationDialog) {
      onEmailConfirmationDialogShouldOpen();
      return;
    }

    if (isInLibrary) {
      removeFromLibrary();
      return;
    }

    addToLibrary();
  }, [
    shouldDisplayEmailConfirmationDialog,
    isInLibrary,
    removeFromLibrary,
    onEmailConfirmationDialogShouldOpen,
    addToLibrary,
  ]);

  const displayAddToLibraryIcon = () => (
    <BaseIconComponent
      title={title}
      icon={<AddToLibraryIcon isAdded={isInLibrary} />}
      onClick={handleClick}
    />
  );

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      {displayEmailConfirmationDialog()}
      {displayAddToLibraryIcon()}
    </Box>
  );
};

export default DisplayAddToLibraryIcon;
